import * as React from 'react'
import moment from 'moment'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, InputText, InputTextArea, Panel, Select } from '../../../atoms'
import { Form } from '../../../molecules'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SvgIcon from 'components/atoms/SvgIcon'
import { BODY_MAX_LENGTH } from './constants'

declare var window: interfaces.IWindow

// 施行日時は date + time を分離して持つので Form の管理対象外
const FIELDS = {
  name: 'name',
  message: 'message',
  body: 'body',
}

// effective_at (date + time) を「未来か過去か」判定するための utility
const isFutureDateTime = (date: string, time: string): boolean => {
  if (!date) {
    return false
  }
  const datetime = new Date(`${date}T${time || '00:00'}:00`)
  if (isNaN(datetime.getTime())) {
    return false
  }
  return datetime.getTime() > Date.now()
}

// 施行日時に応じて active 状態のラベルを「公開予定」「施行中」に切り替える
const getActiveLabel = (date: string, time: string): string => {
  if (!date) {
    return '公開予定 / 施行中'
  }
  return isFutureDateTime(date, time) ? '公開予定' : '施行中'
}

interface IProps {
  terms_version: IJsonResponse
  user: IJsonResponse
}

const AgentTermsVersionEdit: React.FC<IProps> = props => {
  const { data: userData } = utilService.getDataFromJson(props.user)
  const { data: termsVersion } = agentService.getDataFromJson(props.terms_version)

  const isActive = termsVersion.status === 'active'
  // 公開予定 (active かつ施行日時が未来) は下書きに戻せる
  const isScheduled = termsVersion.display_status === 'scheduled'
  // 施行中 (active かつ施行日時が過去) は下書きに戻せない
  const isInEffect = termsVersion.display_status === 'in_effect'
  const [status, setStatus] = React.useState<string>(termsVersion.status)
  const [effectiveDate, setEffectiveDate] = React.useState<string>(() =>
    termsVersion.effective_at ? moment(termsVersion.effective_at).format('YYYY-MM-DD') : ''
  )
  const [effectiveTime, setEffectiveTime] = React.useState<string>(() =>
    termsVersion.effective_at ? moment(termsVersion.effective_at).format('HH:mm') : '00:00'
  )
  const [errors, setErrors] = React.useState<{ [key: string]: null | string }>({})

  // 施行中 (active かつ施行日時が過去) は下書きへ戻せないため draft を選択肢から除外する
  const canRevertToDraft = !isActive || isScheduled
  const statusOptions = React.useMemo(
    () =>
      [
        canRevertToDraft ? { value: 'draft', label: '下書き' } : null,
        { value: 'active', label: getActiveLabel(effectiveDate, effectiveTime) },
        { value: 'deleted', label: '削除済み' },
      ].filter((option): option is { value: string; label: string } => option !== null),
    [canRevertToDraft, effectiveDate, effectiveTime]
  )

  const handleSubmit = React.useCallback(
    async (_initialValues, values) => {
      const effectiveAt = effectiveDate ? `${effectiveDate} ${effectiveTime || '00:00'}` : ''
      const result = { ...values, status, effective_at: effectiveAt }
      const { flush } = await agentService.updateTermsVersion(termsVersion.id, result)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      if (flush.type === 'success') {
        location.href = '/agent/terms_versions'
      }
    },
    [status, effectiveDate, effectiveTime]
  )

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel title={`編集 - ガイド利用規約 ${termsVersion.name}`}>
          {isScheduled && (
            <S.Notice>
              公開予定 (status = active) のため、名前・本文・施行日時は編集できません。
              内容を変更したい場合は、状態を「下書き」に戻すと編集可能になります。
              (メッセージのみそのまま編集可能)
            </S.Notice>
          )}
          {isInEffect && (
            <S.Notice>
              公開済み (status = active) のため、名前・本文・施行日時は編集できません。
              内容を変更したい場合は新しい規約バージョンを作成してください。
              (メッセージのみ編集可能)
            </S.Notice>
          )}
          <Form
            fields={FIELDS}
            handleSubmit={handleSubmit}
            handleUpdateForm={updatedErrors => setErrors(updatedErrors)}
          >
            <InputText
              required={true}
              name="name"
              defaultValue={termsVersion.name || ''}
              label="名前 (社内管理用)"
              disabled={isActive}
              error={errors.name}
            />
            <S.FormItemWrapper>
              <InputTextArea
                required={false}
                name="message"
                defaultValue={termsVersion.message || ''}
                label="メッセージ (説明文)"
                placeholder="個人情報に関わる箇所について、データの取扱について定義を見直しました。具体的には..."
                error={errors.message}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputTextArea
                required={true}
                name="body"
                defaultValue={termsVersion.body || ''}
                label="規約本文"
                placeholder={'第1条 (総則)\n本規約は、...\n\n第2条 (定義)\n本規約において...'}
                disabled={isActive}
                maxLength={BODY_MAX_LENGTH}
                error={errors.body}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <S.DatetimeLabel>施行日時</S.DatetimeLabel>
              <S.DatetimeRow>
                <S.DateInput
                  type="date"
                  value={effectiveDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEffectiveDate(e.target.value)
                  }
                  disabled={isActive}
                />
                <S.TimeInput
                  type="time"
                  value={effectiveTime}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEffectiveTime(e.target.value)
                  }
                  disabled={isActive}
                />
              </S.DatetimeRow>
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Select
                name="status"
                label="状態"
                value={status}
                options={statusOptions}
                onChangeHandler={event => setStatus(event.target.value)}
              />
            </S.FormItemWrapper>
            <div className="Buttons">
              <Button>
                <a href="/agent/terms_versions">
                  <SvgIcon
                    name="keyboard_arrow_left"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  {I18n.t('generic.back_to_index')}
                </a>
              </Button>
              <Button primary={true}>{I18n.t('generic.update')}</Button>
            </div>
          </Form>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
`

S.DatetimeLabel = styled.div`
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: bold;
  color: ${constants.COLORS.subText};
`

S.DatetimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

// ota_reservations の催行日時編集と同じ見た目に揃える
S.DateInput = styled.input`
  padding: 8px;
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 4px;
  font-size: 14px;
  &:disabled {
    background: ${constants.COLORS.gray};
    cursor: not-allowed;
  }
`

S.TimeInput = styled.input`
  padding: 8px;
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 4px;
  font-size: 14px;
  &:disabled {
    background: ${constants.COLORS.gray};
    cursor: not-allowed;
  }
`

S.Notice = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fff8e1;
  border-left: 4px solid #f5b800;
  font-size: 13px;
  line-height: 1.6;
`

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .Buttons {
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: 48px;

    .Button[type='submit'] {
      width: 160px;
    }

    .Button[type='button'] {
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;

      .icon {
        margin-left: -10px;
      }
    }
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    padding: 16px 12px;
  }
`

export default AgentTermsVersionEdit
